import React, { useCallback, useMemo } from 'react';
import { Chip, Link, Tooltip } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { HotelRounded } from '@material-ui/icons';
import MessageIcon from '@material-ui/icons/Message';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { Link as RouterLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { withTheme } from '@/se/theme';
import ChatButton from '../../chat/ChatButton';
import { CaretakerChatDialog } from '../../chat/ChatDialog';
import { PatientTypeIconSwitch } from '../../inputs/PatientTypeButtonGroup';
import { isEmpty } from 'lodash/fp';
import { createStyles, makeStyles, IconButton } from '@material-ui/core';
import useTheme from '@material-ui/core/styles/useTheme';
import { useScopeHospitalId } from '@/hooks/useScopeHospitalId';
import { shouldHighlightPhysicianInOr } from '../../pages/kiosk/schedule/shared/procedureUtils';
import { useMonitorMode } from '@/contexts/MonitorModeContext';
import { PrimarySpecialtyColors } from '@/types/StaffMember';

const useStyles = makeStyles(theme =>
  createStyles({
    chatButton: {
      paddingLeft: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    nurseChip: {
      flexShrink: 0,
      marginLeft: theme.spacing(1),
      height: '1.15em',
      maxWidth: '50%',
      fontSize: '0.85em',
      backgroundColor: theme.palette.action.hover,
      '& .MuiChip-label': {
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(1),
        lineHeight: 1,
      },
      '& .MuiChip-icon': {
        fontSize: '1em',
        marginLeft: theme.spacing(0.5),
        width: '1em',
        height: '1em',
      },
    },
    physicianRow: {
      display: 'inline-flex',
      alignItems: 'center',
      minWidth: 0,
      maxWidth: '100%',
    },
  })
);

export const Container = styled.h4`
  display: flex;
  flex-wrap: ${props => (props.allowWrap ? 'wrap' : 'none')};
  justify-content: space-between;
  margin: 0;
  flex: 1;

  ${props =>
    props.miniView &&
    css`
      flex-direction: column;
    `}
`;

export const Info = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
  max-width: 100%;
  font-size: ${props =>
    props.$expandedOrTileView ? '2.25rem' : props.size === 'large' ? '2.25em' : '1.5em'};
  line-height: ${props => (props.$expandedOrTileView ? '1.15' : '1.4')};
  font-weight: ${props => (props.$expandedOrTileView ? 600 : 'inherit')};
  color: ${withTheme(theme => theme.textColor.string())};
  display: flex;

  @media (max-height: 900px) {
    font-size: ${props =>
      props.$expandedOrTileView ? '1.875rem' : props.size === 'large' ? '2.25em' : '1rem'};
  }

  @media (max-height: 600px) {
    font-size: ${props =>
      props.$expandedOrTileView ? '1.5rem' : props.size === 'large' ? '2.25em' : '2vh'};
    line-height: ${props => (props.$expandedOrTileView || props.size === 'large' ? '1' : '1.4')};
  }

  ${props =>
    props.miniView &&
    css`
      font-size: 1rem;
      line-height: 1.4;
    `}
`;

const InfoRight = styled(Info)`
  flex-grow: ${props => (props.grow ? 1 : 0)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  ${props =>
    props.$compact && '--monitor-badge-size: 0.35em; --monitor-badge-padding: 0 0.4em; --monitor-badge-height: 70%;'}

  ${props =>
    props.miniView &&
    css`
      margin-left: 0;
      color: ${withTheme(theme => theme.secondaryTextColor.string())};
      opacity: 0.8;
    `}
`;

const Physician = styled.div`
  display: inline-block;
  min-width: 0;
  flex: 0 1 auto;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  color: ${props => props.color || 'inherit'};
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  color: #003c0a;
  background: #43be57;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

/**
 * Specialized tag component for "Ready For Surgeon" status that's only used
 * for select hospitals.
 *
 * This should only be rendered when:
 * - The hostname is 'view5.ospitek.com' AND
 * - The hospitalId is either 1 or 2
 *
 */
const AltReadyForSurgeonTag = styled.div`
  display: flex;
  align-items: center;
  color: black;
  background: #00a7f7;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

const ReadyForAnesthesiaTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: purple;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

const SpinalTimeoutTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: #000000;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;
const DelayTag = styled.div`
  display: flex;
  align-items: center;
  color: #003c0a;
  background: #ffbd00;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

const AlertTag = styled.div`
  display: flex;
  align-items: center;
  color: #003c0a;
  background: #b13030;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

export const ChairTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: ${props => (props.delayed ? '#ffc100' : 'cornflowerblue')};
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

export const PTTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: ${props => (props.delayed ? '#ffc100' : 'cornflowerblue')};
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

export const MedsDeliveredTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: #17a2b8;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

export const ExternalPharmacyTag = styled.div`
  display: flex;
  align-items: center;
  color: white;
  background: #ff9800;
  border-radius: 0.1875rem;
  font-size: var(--monitor-badge-size, 0.5em);
  padding: var(--monitor-badge-padding, 0 0.5em);
  margin-left: 0.6em;
  height: var(--monitor-badge-height, 80%);
`;

const containsWord = (str, word) => str?.match(new RegExp('\\b' + word + '\\b')) != null;

const getPhysicianPrefix = physician => (containsWord(physician?.toLowerCase(), 'dr') ? '' : 'Dr. ');

const PatientSlotInfo = ({
  physician,
  bed,
  showBedNumber,
  patient,
  patientId,
  patientType,
  procedure,
  size,
  readyForOr,
  readyForSurgeon,
  readyInOr,
  familyReady,
  readyForPickup,
  isHoldProcedure,
  isBlockNerve,
  called,
  transferredToChair,
  transferredToPT,
  ptComplete,
  allowWrap,
  miniView,
  isDPU,
  isPreOp,
  isSuperAdmin,
  messagingView,
  patientData,
  grow,
  readyForAnesthesia,
  isSpinalTimeout,
  timeEntered,
  room,
  showLatePreOpIndicator,
  showPacuLateIndicator,
  isGroupAdmin,
  medsDeliveryStatus,
  expandedOrTileView,
  screenType,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const hospitalId = useScopeHospitalId();
  const { isKiosk } = useMonitorMode();

  const hasCaretakerConsent = patientData?.caretakerConsent;
  const hasCaretakerNotificationNumber = patientData ? Boolean(patientData?.notificationNumbers) : undefined;
  // PreOp nurse only on PreOp panels/tablet (does not show on PACU/PostOp)
  const assignedNurse = isPreOp && !miniView && !messagingView ? patientData?.preOpNurse : null;

  const isAltReadyForSurgeon =
    (window.location.hostname === 'view5.ospitek.com' && (Number(hospitalId) === 1 || Number(hospitalId) === 2)) ||
    (window.location.hostname === 'testing.ospitek.com' && Number(hospitalId) === 152);

  const highlightPhysician = shouldHighlightPhysicianInOr({
    orId: room?.procedure?.orId,
    physicianCurrentRoomId: room?.procedure?.physician?.currentRoomId,
  });

  const physicianPrefix = useMemo(() => getPhysicianPrefix(physician?.replaceAll('.', '')), [physician]);

  const patientOnClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  const chairDelayed = useMemo(
    () => isDelayed(timeEntered, transferredToChair, patientData?.room?.oobWarningThreshold),
    [patientData?.room?.oobWarningThreshold, timeEntered, transferredToChair]
  );

  const ptDelayed = useMemo(
    () => isDelayed(timeEntered, transferredToPT, patientData?.room?.ptWarningThreshold),
    [patientData?.room?.ptWarningThreshold, timeEntered, transferredToPT]
  );

  return (
    <Container allowWrap={allowWrap} miniView={miniView}>
      <Info
        style={{ minWidth: 0, marginRight: '1rem' }}
        size={size}
        miniView={miniView}
        $expandedOrTileView={expandedOrTileView}
      >
        <Box className={classes.physicianRow}>
          <Physician color={highlightPhysician ? theme.palette.success.main : undefined}>
            {!isEmpty(physician) && physicianPrefix}
            {physician}
          </Physician>
          {assignedNurse?.name && (
            <Chip
              className={classes.nurseChip}
              icon={
                <RadioButtonUncheckedIcon
                  style={{
                    color:
                      (assignedNurse?.primarySpecialty
                        ? PrimarySpecialtyColors[assignedNurse.primarySpecialty]
                        : undefined) ?? theme.palette.error.main,
                  }}
                />
              }
              size="small"
              label={assignedNurse.name}
            />
          )}
        </Box>
      </Info>
      <InfoRight
        size={size}
        miniView={miniView}
        grow={grow || showBedNumber}
        $compact={!isKiosk}
        $expandedOrTileView={expandedOrTileView}
      >
        {patientId && !isKiosk ? (
          <Link
            component={RouterLink}
            to={`${isSuperAdmin || isGroupAdmin ? `/su/${hospitalId}` : ''}/patients/${patientId}`}
            onClick={e => e.stopPropagation()}
            target="_blank"
            style={{
              color: 'inherit',
            }}
          >
            {patient}
          </Link>
        ) : (
          patient
        )}
        {messagingView && !!patient && (
          <Box className={classes.chatButton}>
            {hasCaretakerConsent && hasCaretakerNotificationNumber ? (
              <ChatButton
                hasUnreadMessages={patientData?.caretakerThread?.hasUnreadMessages}
                chatComponent={CaretakerChatDialog}
                fontSize="inherit"
                chatProps={{
                  patient: patientData,
                }}
              />
            ) : (
              <Tooltip
                title={
                  !hasCaretakerConsent
                    ? 'No caretaker consent.'
                    : !hasCaretakerNotificationNumber
                      ? 'No caretaker phone number.'
                      : 'Unexpected error occurred.'
                }
              >
                <div>
                  <IconButton color="primary" size="small" edge="end" disabled>
                    <MessageIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </Box>
        )}
        {!!bed && showBedNumber && (
          <Box display="flex" alignItems="center" style={{ gap: '0.5em' }}>
            <Box>
              <Divider orientation="vertical" />
            </Box>
            <HotelRounded fontSize="inherit" /> {bed}
          </Box>
        )}
        <PatientTypeIconSwitch patientType={patientType} />
        {readyForAnesthesia && !miniView && !isDPU && !isBlockNerve && !messagingView && (
          <ReadyForAnesthesiaTag>Ready For Anes</ReadyForAnesthesiaTag>
        )}
        {isSpinalTimeout && !miniView && !isDPU && !messagingView && (
          <SpinalTimeoutTag>Spinal Timeout</SpinalTimeoutTag>
        )}
        {readyForSurgeon &&
          !miniView &&
          !isDPU &&
          !readyForOr &&
          !messagingView &&
          (isAltReadyForSurgeon ? (
            <AltReadyForSurgeonTag>Ready For Surgeon</AltReadyForSurgeonTag>
          ) : (
            <Tag>Ready For Surgeon</Tag>
          ))}
        {readyForOr && !miniView && !isDPU && !messagingView && <Tag>Ready For OR</Tag>}
        {readyInOr && !miniView && !messagingView && <Tag>Ready For Surgery</Tag>}
        {showLatePreOpIndicator && !miniView && !messagingView && <AlertTag>LATE</AlertTag>}
        {showPacuLateIndicator && !miniView && !messagingView && <AlertTag>LATE</AlertTag>}
        {familyReady && !miniView && !messagingView && <Tag>Family Ready</Tag>}
        {readyForPickup && !miniView && !messagingView && <Tag>Ready For Pickup</Tag>}
        {isHoldProcedure && !miniView && !messagingView && <DelayTag>On Hold</DelayTag>}
        {isBlockNerve && !miniView && !messagingView && <DelayTag>Nerve Block</DelayTag>}
        {called && !miniView && isPreOp && !messagingView && <AlertTag>Called</AlertTag>}
        {transferredToChair && !transferredToPT && !miniView && !messagingView && (
          <ChairTag delayed={chairDelayed}>OoB</ChairTag>
        )}
        {transferredToPT && !ptComplete && !miniView && !messagingView && <PTTag delayed={ptDelayed}>PT</PTTag>}
        {ptComplete && !miniView && !messagingView && (
          <PTTag delayed={isDelayed(timeEntered, ptComplete, patientData?.room?.ptCompleteWarningThreshold)}>
            PT Complete
          </PTTag>
        )}
        {medsDeliveryStatus === 'Delivered' && !miniView && !messagingView && (
          <MedsDeliveredTag>Meds Delivered</MedsDeliveredTag>
        )}
        {medsDeliveryStatus === 'ExternalPharmacy' && !miniView && !messagingView && (
          <ExternalPharmacyTag>External Pharmacy</ExternalPharmacyTag>
        )}
      </InfoRight>
    </Container>
  );
};

export const isDelayed = (timeEntered, timeChanged, threshold) =>
  timeEntered &&
  timeChanged &&
  threshold &&
  new Date(timeChanged).getTime() - new Date(timeEntered).getTime() > threshold * 60 * 1000;

export default PatientSlotInfo;
